// @ts-nocheck

import _ from 'lodash';
import React from 'react';
import { Button } from 'react-bootstrap';
import Payment from 'sharedComponents/Payment';
import { methodNames } from 'sharedComponents/Payment/PaymentMethodMenu';
import Spinner from 'sharedComponents/Spinner';
import FundDisclaimer from 'sharedComponents/FundDisclaimer';
import { LayoutDisclaimerAboveSsl } from 'modules/renderDisclaimerText';
import Contact from './sections/Contact';
import Options from './sections/Options';
import { sendGtmPixel } from "modules/gtm";

/** Merged into layout for STS mobile-guest narrow contact (name, email, zip) — matches DonateDefault */
const stsCompactContactLayoutConfig = {
  contact: {
    billingAddressCheckbox: { visible: false },
    firstName: { xs: 6, sm: 6, md: 6 },
    lastName: { xs: 6, sm: 6, md: 6 },
    email: { xs: 12, sm: 12, md: 12 },
    zip: { xs: 12, sm: 12, md: 12 },
  },
}

const stepsDefault = [
  'options',
  'contact',
  'contact_2',
  'payment_details',
]

const stepsCompactContact = [
  'options',
  'contact',
  'payment_details',
]

import type { DonateProps, DonateState } from './Donate';
/**
 * Donate Now Page
 * 
 * Used as default redirect from /
 * Renders and rewrite to /donate?amount=50&term=monthly on load.
 * 
 */
class DonateBySteps extends React.Component<DonateProps, DonateState> {

  state = {
    step: 'options',
    validated: false,
  }

  /**
   * STS mobile guests only: one contact step (name / email / zip), no address step.
   * Parent sets stsMobileGuestContact from Donate.state.stsMobileGuest (viewport + guest + STS).
   */
  isStsMobileGuestNarrowContact = () => {
    const { site, stsMobileGuestContact } = this.props
    return site === 'STS' && !!stsMobileGuestContact
  }

  getStepSequence = () => {
    return this.isStsMobileGuestNarrowContact() ? stepsCompactContact : stepsDefault
  }

  validateWithinComponent = () => {
    const { paymentType, donationAmount } = this.props
    const { step } = this.state
    switch (step) {
      case 'options':
        return donationAmount >= 5
      case 'payment_method':
        return !!paymentType
    }
    return true
  }

  validateAndGoNext = (result) => {
    const { errors = [] } = result
    const { step } = this.state
    const compactContact = this.isStsMobileGuestNarrowContact()
    let validated = true, nextStep = step
    switch (step) {
      case 'payment_details':
        nextStep = 'payment_details'
        validated = !errors.find(e => e.field === 'card.fullName')
        break
      case 'contact':
        nextStep = compactContact ? 'payment_details' : 'contact_2'
        if (compactContact) {
          validated = !errors.find(e =>
            ['firstName', 'lastName', 'email', 'address.zip', 'inspiration'].includes(e.field)
          )
        } else {
          validated = !errors.find(e => ['inspiration', 'firstName', 'lastName', 'email', 'phone'].includes(e.field))
        }
        break
      case 'contact_2':
        nextStep = 'payment_details'
        validated = !errors.find(e => [
          'address.streetAddress', 'address.city', 'address.state', 'address.zip', 'address.country'
        ].includes(e.field))
    }
    if (validated) {
      this.props.setDisplayValidationError(false)
      this.setState({ 
        validated: false, 
        step: nextStep, 
        forceCheckValidity: false, 
      })
    }
    else {
      this.props.setDisplayValidationError(true)
      this.setState({ 
        validated, 
        forceCheckValidity: false, 
      })
    }
  }

  componentDidUpdate(prevProps: Readonly<DonateProps>, prevState: Readonly<DonateState>, snapshot?: any): void {
    if (this.props.paymentRequestError && !prevProps.paymentRequestError) {
      this.setState({ step: 'payment_details' })
    }
    if (
      prevProps.stsMobileGuestContact
      && !this.props.stsMobileGuestContact
      && this.state.step === 'payment_details'
    ) {
      this.setState({ step: 'contact_2' })
    }
    if (this.state.step !== prevState.step) {
      window.scrollTo(0, 0)
      try {
        window.parent.postMessage("scrollToIframe", "https://savethestorks.org")
      }
      catch(error) {
        console.error('error scrolling up', error)
      }
      sendGtmPixel({
        event: 'donate_by_step',
        step: this.state.step,
        prev_step: prevState.step,
      })
      const seq = this.getStepSequence()
      if (seq.indexOf(this.state.step) > seq.indexOf(prevState.step)) {
        this.props.setDisplayValidationError(false)
      }
    }
  }

  componentDidMount(): void {
    sendGtmPixel({
      event: 'donate_by_step',
      step: 'options',
    })
    sessionStorage.setItem('layout', 'by_step')
  }

  render() {
    const {
      customer, 
      getAccessTokenSilently, 
      guestMode, 
      isAdminTerminal,
      loadingCustomer, 
      site, 
      layoutConfig,
      paymentType,
      loading,
    } = this.props
    const { step, forceCheckValidity, validated } = this.state
    const validatedWithinComponent = this.validateWithinComponent()
    const stepSequence = this.getStepSequence()
    const compactContact = this.isStsMobileGuestNarrowContact()
    const mergedLayoutConfig = compactContact
      ? _.merge({}, layoutConfig, stsCompactContactLayoutConfig)
      : layoutConfig
    const containerStyle = mergedLayoutConfig?.container?.style || {}
    const ctaAdditionalMessage = mergedLayoutConfig?.paymentDetails?.ctaAdditionalMessage ? (
      <div className="pre-field mb1">
        { mergedLayoutConfig?.paymentDetails?.ctaAdditionalMessage }
      </div>
    ) : null
    return (
      <div 
        className='py2 px2 guest-mode' 
        style={{ 
          maxWidth: 540, 
          margin: 'auto', 
          ...containerStyle,
        }}
      >
        <style>{`#app-root-${site},html { background: ${containerStyle.backgroundColor || 'white'}; padding: ${containerStyle.padding || '0px'}; }`}</style>
        <style>{containerStyle.additionalStyles || ''}</style>
        {/* {
          containerStyle.donateButtonBorder && (
            <style>{`.btn-donate { border: ${containerStyle.donateButtonBorder} !important }`}</style>
          )
        }
        {
          containerStyle.errorMessageColor && (
            <style>{`.error-message, .invalid-feedback { color: ${containerStyle.errorMessageColor} !important }`}</style>
          )
        }
        {
          containerStyle.errorMessageBackgroundColor && (
            <style>{`.error-message, .invalid-feedback { background-color: ${containerStyle.errorMessageBackgroundColor} !important }`}</style>
          )
        }
        {
          containerStyle.subtextColor && (
            <style>{`label>span, .pre-field, .form-check-label { color: ${containerStyle.subtextColor} !important }`}</style>
          )
        } */}
        { loading && <Spinner mask/> }
        {
          step === 'options' ? null : (
            <div>
              <Button 
                variant='light'
                size='lg'
                // className='bg-white border-white darkblue'
                style={{ 
                  paddingLeft: 0,
                  color: containerStyle.color || '#0d5e7d',
                  border: 'none',
                  // borderColor: containerStyle.color || 'white',
                  backgroundColor: containerStyle.backgroundColor || 'white',
                 }}
                onClick={() => {
                  this.setState({ step: stepSequence[stepSequence.indexOf(step) - 1] })
                }}
              >
                <span className='fa fa-arrow-left'/>
              </Button>
              <Button 
                variant='light' 
                size='lg'
                // className='bg-white border-white float-right darkblue'
                className='float-right'
                style={{ 
                  paddingRight: 0,
                  color: containerStyle.color || '#0d5e7d',
                  border: 'none',
                  backgroundColor: containerStyle.backgroundColor || 'white',
                }}
                onClick={() => this.setState({ step: 'options' })}
              >
                &#x2715;
              </Button>
            </div>
          )
        }
        <div style={step === 'options' ? null : { display: 'none' }}>
          <Options 
            {...this.props}
            guestMode={true} 
            isAdminTerminal={isAdminTerminal}
            layoutConfig={mergedLayoutConfig}
            initialDonationType={mergedLayoutConfig?.initialDonationType}
            onlyShowTypes={mergedLayoutConfig?.onlyShowTypes}
          />
        </div> 
        {/* payment method step is currently skipped and default to CC */}
        {/* <div style={step === 'payment_method' ? null : { display: 'none' }}>
          <PaymentMethodMenu 
            {...this.props}
            layoutConfig={layoutConfig}
            canMakePayment={canMakePayment}
          />
        </div>  */}
        <div style={['contact', 'contact_2'].includes(step) ? null : { display: 'none' }}>
          <Contact
            {...this.props}
            layoutConfig={mergedLayoutConfig}
            title={step === 'contact_2' ? mergedLayoutConfig.contact.title2 : null}
            guestMode={guestMode} 
            editMode={true} 
            visibleInputs={
              compactContact && step === 'contact'
                ? ['firstName', 'lastName', 'email', 'zip']
                : step === 'contact'
                  ? ['firstName', 'lastName', 'phone', 'email', 'company']
                  : ['streetAddress', 'country', 'state', 'city', 'zip']
            }
            campaignMenuVisible={
              compactContact
                ? (mergedLayoutConfig?.campaignMenuVisible ?? false)
                : (mergedLayoutConfig?.campaignMenuVisible ?? step === 'contact')
            }
            autoComplete={false}
          />
        </div>
        <div style={['payment_details'].includes(step) ? null : { display: 'none' }}>
          <Payment 
            {...this.props}
            layoutConfig={mergedLayoutConfig}
            getAccessTokenSilently={getAccessTokenSilently} 
            withTitle={!customer && !loadingCustomer} 
            titleText={methodNames[paymentType]}
            guestMode={guestMode} 
            // showFormOnly={step === 'payment_details'}
            showFormOnly={false}
            toastVisible={false}
            updateStripeCanMakePayment={canMakePayment => this.setState({ canMakePayment })}
            sslVisible={true}
            forceCheckValidity={forceCheckValidity}
            checkValidityCallback={(res) => {
              this.validateAndGoNext(res)
            }}
            showProcessingFee={true}
            paymentCardStyle={mergedLayoutConfig?.paymentDetails?.paymentCardStyle || null}
          />
        </div>
        {
          step === 'payment_details' ? null : (
            <>
              <br />
              <Button
                variant='primary'
                className='btn-donate mx-auto full-width'
                disabled={!validatedWithinComponent}
                onClick={() => {
                  const contactStepsToValidate = compactContact ? ['contact'] : ['contact', 'contact_2']
                  if (!validated && contactStepsToValidate.includes(step)) {
                    this.setState({ forceCheckValidity: true })
                  }
                  else if (validatedWithinComponent) {
                    this.setState({ step: stepSequence[stepSequence.indexOf(step) + 1] })
                    this.props.setDisplayValidationError(false)
                  }
                }}
              >
                { step === 'options' ? (mergedLayoutConfig.startButtonText || 'Give') : 'Continue' }
              </Button>
              {
                mergedLayoutConfig.startButtonAdditionalText ? (
                  <div className='mt1' style={{ fontSize: 12 }}>
                    {mergedLayoutConfig.startButtonAdditionalText}
                  </div>
                ) : <br />
              }
              <br />
              { ctaAdditionalMessage }
              <LayoutDisclaimerAboveSsl layoutConfig={mergedLayoutConfig} />
              <legend className="pre-field" style={mergedLayoutConfig?.sslContent?.container?.style || { fontSize: 12 }}>
                <span 
                  className={mergedLayoutConfig?.sslContent?.icon?.className || "fa fa-lock"}
                  style={mergedLayoutConfig?.sslContent?.icon?.style || null}
                />&nbsp;
                <span style={mergedLayoutConfig.sslContent?.text?.style}>
                  {mergedLayoutConfig.sslContent?.text?.content || 'SSL encrypted and secure'}
                </span>
              </legend>
            </>
          )
        }
        <FundDisclaimer site={site} guestMode={true} layoutConfig={mergedLayoutConfig}/>
      </div>
    )
  }
}

export default DonateBySteps
